import './App.css';
import React, { useEffect, useState } from 'react';
// MSAL imports
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { getUser, i18n, MainLayout, useAppDispatch } from 'quil';
import { I18nextProvider } from 'react-i18next';
import { ReactKeycloakProvider, useKeycloak } from '@react-keycloak/web'
import {
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import CustomNavigationClient from './utils/NavigationClient';
import { loginRequest } from './authConfig';
import Dashboard from './pages/Dashboard';
import Assays from './pages/Assays';
import AssayEditor from './pages/AssayEditor';
import Experiments from './pages/Experiments';
import ExperimentEditor from './pages/ExperimentEditor';
import Login from './pages/Login';
import Settings from './pages/Settings';
import 'katex/dist/katex.min.css';
import NavigateOnError from './pages/Error/NavigateOnError';
import ErrorPage from './pages/Error/ErrorPage';
import keycloak from './keycloak';
import { create } from '@bufbuild/protobuf';
import { UserSchema } from './gen/quanterix/simoa_one/analysis_app_api/v1/user_pb';

function Layout() {
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [outlet, setOutlet] = useState(location.pathname);
  // const [navOpen, setNavOpen] = useState(false);
  const { instance } = useMsal();

  let activeAccount;

  if (instance) {
    activeAccount = instance.getActiveAccount();
  }

  const activeUser = activeAccount && activeAccount.name;

  const dispatch = useAppDispatch();  
  


  useEffect(() => {
    // Update the app bar based on the current route
    const currentPath = location.pathname;
    setOutlet(currentPath);
  }, [location]);

  // const handleToggleNav = () => {
  //   setNavOpen((prevState) => !prevState);
  // };

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch((error) => console.log(error));
  };

  // const handleLogout = () => {
  //   instance.logoutRedirect().catch((error) => console.log(error));
  // };

  // const handleLoginSso = () => {
  //   handleLogin;
  // };

  const navigate = useNavigate();

  const handleNavigate = (navTo: string) => {
    navigate(navTo);
  };

  const {keycloak: keycloakResponse, initialized} = useKeycloak();

  const isTestEnv: boolean = import.meta.env.VITE_REACT_APP_PLAYWRIGHT;

  // randomly generated test UUID to use for local testing. Change the userId to reset the EULA agreement.
  const userId = isTestEnv ? "1d088c41-ec4a-4201-ade6-67cc1055782f" : keycloakResponse.idTokenParsed?.sub;
  const tenantId = isTestEnv ? "1d088c41-ec4a-4201-ade6-67cc1055782d" : keycloakResponse.idTokenParsed?.organization[0]

  // Load user once we have identifiers
  useEffect(() => {
    if (!userId || !tenantId) return;
    const userReq = create(UserSchema, { id: userId, tenantId });    
    dispatch(getUser(userReq));
  }, [userId, tenantId, dispatch]);

    return isTestEnv ? (
        <MainLayout
            content={<Outlet />}
            onNavTo={handleNavigate}
            activeUser={activeUser || ''}
        />
    ) : (
        <>
        {keycloakResponse.authenticated ?
                <MainLayout
                   content={<Outlet />}
                   onNavTo={handleNavigate}
                   activeUser={keycloakResponse.tokenParsed?.preferred_username || ''}
                        />  : <></>}
        </>
    );
}

type AppProps = {
  pca: IPublicClientApplication;
};

function App({ pca }: AppProps) {
  const navigate = useNavigate();
  const navigationClient = new CustomNavigationClient(navigate);
  pca.setNavigationClient(navigationClient);
  const initOptions = {
    onLoad: 'login-required',
    pkceMethod: 'S256',
    scope: 'organization'
  }

  return (
      <ReactKeycloakProvider authClient={keycloak} initOptions={initOptions}>
          <I18nextProvider i18n={i18n}>
              <Routes>
                  <Route path="/" element={<NavigateOnError ><Layout /></NavigateOnError >}>
                      <Route index element={<NavigateOnError ><Dashboard /></NavigateOnError >} />
                      <Route path="assays" element={<NavigateOnError ><Assays /></NavigateOnError>} />
                      <Route path="assays/editor" element={<NavigateOnError><AssayEditor /></NavigateOnError>} />
                      <Route path="experiments" element={<NavigateOnError><Experiments /></NavigateOnError>} />
                      <Route path="experiments/editor" element={<NavigateOnError><ExperimentEditor /></NavigateOnError>} />
                      <Route path="settings" element={<NavigateOnError><Settings /></NavigateOnError>} />
                      <Route path="error" element={<ErrorPage />} />
                      {/* Catch-all route for wrong URLs */}
                      <Route path="*" element={<ErrorPage />} />
                  </Route>
              </Routes>
          </I18nextProvider>
      </ReactKeycloakProvider>
  );
}

export default App;
